// v3.15-1211 ADMIN-007 — Company Documents.
//
// Metadata + versioning surface. Immutable version history, soft-delete
// / archive only. Config-driven categories with role-based permissions.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, FileText, Plus, Trash2, Archive, ArchiveRestore, Download, Eye, History, Upload, Building2, Folder } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';

const fmtDate = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '—');
const fmtBytes = (n) => {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/* ============================== Modals ============================== */

const UploadModal = ({ open, onClose, onSaved, companyId, categories }) => {
  const [title, setTitle]   = useState('');
  const [desc, setDesc]     = useState('');
  const [notes, setNotes]   = useState('');
  const [tags, setTags]     = useState('');
  const [catId, setCatId]   = useState(categories[0]?.id || '');
  const [file, setFile]     = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCatId(categories[0]?.id || ''); }, [categories]);

  const submit = async () => {
    if (!file || !title || !catId) { toast.error('Title, category, and file required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('company_id', companyId);
      fd.append('category_id', catId);
      fd.append('title', title);
      if (desc)  fd.append('description', desc);
      if (notes) fd.append('notes',       notes);
      if (tags)  fd.append('tags',        tags);
      fd.append('file', file);
      await api.post('/company-documents/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded'); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Upload Document</h2>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Category</label>
          <select data-testid="dc-upload-cat" value={catId} onChange={(e) => setCatId(e.target.value)}
                  className="w-full mb-3 border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">— Select category —</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Title</label>
          <Input data-testid="dc-upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Description</label>
          <Input data-testid="dc-upload-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Tags (comma separated)</label>
          <Input data-testid="dc-upload-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Notes</label>
          <Input data-testid="dc-upload-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">File</label>
          <input data-testid="dc-upload-file" type="file"
                 onChange={(e) => setFile(e.target.files?.[0] || null)}
                 className="w-full mb-3 text-sm" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="dc-upload-save" onClick={submit} disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Upload</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NewVersionModal = ({ open, onClose, onSaved, document }) => {
  const [file, setFile]     = useState(null);
  const [notes, setNotes]   = useState('');
  const [saving, setSaving] = useState(false);
  if (!document) return null;
  const submit = async () => {
    if (!file) { toast.error('File required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      if (notes) fd.append('notes', notes);
      fd.append('file', file);
      await api.post(`/company-documents/documents/${document.id}/versions`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('New version uploaded'); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-3">Upload New Version</h2>
          <p className="text-xs text-slate-500 mb-3">{document.title} · currently v{document.current_version_number}</p>
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">Version Notes</label>
          <Input data-testid="dc-newver-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-3" />
          <label className="block text-[11px] font-bold uppercase text-slate-500 mb-1">File</label>
          <input data-testid="dc-newver-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full mb-3 text-sm" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button data-testid="dc-newver-save" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VersionHistoryModal = ({ open, onClose, document }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!open || !document) return;
    setLoading(true);
    api.get(`/company-documents/documents/${document.id}/versions`)
      .then((r) => setRows(r.data?.items || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [open, document]);

  const downloadUrl = (vid, inline = false) => `${api.defaults.baseURL}/company-documents/versions/${vid}/download?inline=${inline}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <div className="p-2">
          <h2 className="text-xl font-extrabold text-slate-900 mb-1">Version History</h2>
          <p className="text-xs text-slate-500 mb-3">{document?.title}</p>
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (
            <table className="w-full text-sm" data-testid="dc-versions-table">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-2 py-1.5 w-16">Ver</th>
                  <th className="text-left px-2 py-1.5">File</th>
                  <th className="text-left px-2 py-1.5">Uploaded</th>
                  <th className="text-left px-2 py-1.5">By</th>
                  <th className="text-right px-2 py-1.5">Size</th>
                  <th className="text-right px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} data-testid={`dc-ver-${v.version_number}`} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 font-bold">v{v.version_number}</td>
                    <td className="px-2 py-1.5 text-xs">{v.file_name}</td>
                    <td className="px-2 py-1.5 text-xs">{fmtDate(v.uploaded_at)}</td>
                    <td className="px-2 py-1.5 text-xs">{v.uploaded_by_email || v.uploaded_by_id?.slice(0, 8)}</td>
                    <td className="px-2 py-1.5 text-xs text-right">{fmtBytes(v.size_bytes)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex justify-end gap-1">
                        <a data-testid={`dc-ver-preview-${v.version_number}`} href={downloadUrl(v.id, true)} target="_blank" rel="noreferrer"
                           className="text-slate-500 hover:text-slate-800 p-0.5" title="Preview">
                          <Eye className="w-4 h-4" />
                        </a>
                        <a data-testid={`dc-ver-download-${v.version_number}`} href={downloadUrl(v.id, false)}
                           className="text-slate-500 hover:text-slate-800 p-0.5" title="Download">
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ============================== Page ============================== */

export default function CompanyDocumentsPage() {
  const [companies, setCompanies]   = useState([]);
  const [companyId, setCompanyId]   = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [documents, setDocuments]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [versionFor, setVersionFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/companies');
        const list = data?.items || data || [];
        setCompanies(list);
        if (list.length > 0) setCompanyId(list[0].id);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const fetchCategories = useCallback(async () => {
    if (!companyId) { setCategories([]); return; }
    try {
      const { data } = await api.get('/company-documents/categories', { params: { company_id: companyId } });
      setCategories(data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('Documents restricted for your role');
    }
  }, [companyId]);

  const fetchDocuments = useCallback(async () => {
    if (!companyId) { setDocuments([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/company-documents/documents', {
        params: {
          company_id: companyId,
          category_id: categoryFilter || undefined,
          include_archived: includeArchived,
        },
      });
      setDocuments(data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('Documents restricted for your role');
    } finally { setLoading(false); }
  }, [companyId, categoryFilter, includeArchived]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const doArchive = async (d) => {
    try {
      await api.post(`/company-documents/documents/${d.id}/archive`);
      toast.success('Archived'); fetchDocuments();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Archive failed'); }
  };
  const doRestore = async (d) => {
    try {
      await api.post(`/company-documents/documents/${d.id}/restore`);
      toast.success('Restored'); fetchDocuments();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };
  const doDelete = async (d) => {
    if (!window.confirm(`Delete "${d.title}"? Version history will remain audit-preserved.`)) return;
    try {
      await api.delete(`/company-documents/documents/${d.id}`);
      toast.success('Deleted'); fetchDocuments();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const downloadCurrent = (d) => {
    window.open(`${api.defaults.baseURL}/company-documents/versions/${d.current_version_id}/download?inline=false`, '_blank');
  };
  const previewCurrent = (d) => {
    window.open(`${api.defaults.baseURL}/company-documents/versions/${d.current_version_id}/download?inline=true`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="company-documents-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Company Documents</h1>
            <p className="text-sm text-slate-500">
              Centralised document management. Metadata + immutable version history. Soft-delete only.
            </p>
          </div>
        </div>
        <Button data-testid="dc-upload-open" onClick={() => setUploadOpen(true)} disabled={!companyId}
                className="bg-blue-600 hover:bg-blue-700 text-white">
          <Upload className="w-4 h-4 mr-2" /> Upload Document
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="dc-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              <Building2 className="w-3 h-3 inline mr-1" /> Company
            </label>
            <select data-testid="dc-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              <Folder className="w-3 h-3 inline mr-1" /> Category
            </label>
            <select data-testid="dc-cat-filter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex items-center pt-6">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="dc-include-archived" type="checkbox" checked={includeArchived}
                     onChange={(e) => setIncludeArchived(e.target.checked)} />
              Include archived
            </label>
          </div>
        </div>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl" data-testid="dc-error">{error}</div>
      ) : loading ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading documents…
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="dc-documents">
          {documents.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="dc-empty">
              No documents yet. Click <span className="font-semibold">Upload Document</span> to add one.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Title</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Tags</th>
                  <th className="text-right px-3 py-2 w-16">Version</th>
                  <th className="text-left px-3 py-2">Last update</th>
                  <th className="text-right px-3 py-2 w-56">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => {
                  const archived = !!d.archived_at;
                  return (
                    <tr key={d.id} data-testid={`dc-doc-${d.id}`} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <div className={'font-semibold ' + (archived ? 'text-slate-400 line-through' : 'text-slate-800')}>
                          {d.title}
                        </div>
                        {d.description && <div className="text-xs text-slate-500">{d.description}</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full">
                          {d.category_name || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {(d.tags || []).map((t) => (
                          <span key={t} className="inline-block bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded mr-1">{t}</span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right font-bold">v{d.current_version_number}</td>
                      <td className="px-3 py-2 text-xs">{fmtDate(d.updated_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button data-testid={`dc-preview-${d.id}`} onClick={() => previewCurrent(d)}
                                  className="text-slate-500 hover:text-slate-800 p-1" title="Preview">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button data-testid={`dc-download-${d.id}`} onClick={() => downloadCurrent(d)}
                                  className="text-slate-500 hover:text-slate-800 p-1" title="Download">
                            <Download className="w-4 h-4" />
                          </button>
                          <button data-testid={`dc-history-${d.id}`} onClick={() => setHistoryFor(d)}
                                  className="text-slate-500 hover:text-slate-800 p-1" title="Version history">
                            <History className="w-4 h-4" />
                          </button>
                          {!archived && (
                            <button data-testid={`dc-newver-${d.id}`} onClick={() => setVersionFor(d)}
                                    className="text-slate-500 hover:text-slate-800 p-1" title="New version">
                              <Upload className="w-4 h-4" />
                            </button>
                          )}
                          {archived ? (
                            <button data-testid={`dc-restore-${d.id}`} onClick={() => doRestore(d)}
                                    className="text-emerald-600 hover:text-emerald-800 p-1" title="Restore">
                              <ArchiveRestore className="w-4 h-4" />
                            </button>
                          ) : (
                            <button data-testid={`dc-archive-${d.id}`} onClick={() => doArchive(d)}
                                    className="text-amber-600 hover:text-amber-800 p-1" title="Archive">
                              <Archive className="w-4 h-4" />
                            </button>
                          )}
                          <button data-testid={`dc-delete-${d.id}`} onClick={() => doDelete(d)}
                                  className="text-rose-500 hover:text-rose-700 p-1" title="Delete (logical)">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} onSaved={fetchDocuments}
                    companyId={companyId} categories={categories} />
      <NewVersionModal open={!!versionFor} onClose={() => setVersionFor(null)} onSaved={fetchDocuments} document={versionFor} />
      <VersionHistoryModal open={!!historyFor} onClose={() => setHistoryFor(null)} document={historyFor} />
    </div>
  );
}
